/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import * as XLSX from 'xlsx';

import { Inject, Injectable, LOCALE_ID } from '@angular/core';

import { distinctUntilChanged, filter, map, mergeMap, switchMapTo, tap, withLatestFrom } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

import {
    DataReportCollection,
    FeatureReportCollection,
    FeatureReportDefinitionCollection,
    FeatureReportKeys,
    ReportDefinition,
    ReportField,
    ReportStateModel,
    ReportTypeValue,
    SelectReportByNamePayload,
} from '../state/report.model';
import { RestService } from '../../shared/services/rest.service';
import { DownloadService } from '../../shared/services/download.service';
import { DashGisUtil } from '../../shared/dash-gis-util';
import { LoggerService } from '../../shared/services/logger.service';
import { ReportState } from '../state/report.state';
import { SelectionState } from '../../map/state/selection.state';
import { SelectionFeaturePayload, SelectionFeatures } from '../../map/state/selection.model';
import { LoadDataReports, SelectReportByName, SetFeatureReportKeys } from '../state/report.actions';
import { MapCreated } from '../../map/state/active-map.actions';
import { isEqual } from 'lodash-es';
import { AuthState } from '../../../core/security/state/authState.state';
import { formatNumber } from '@angular/common';
import { DropDownDataOptions, DropDownItem } from '../state/dataForm.model';
import { Actions, ofActionSuccessful, Select, Store } from '@ngxs/store';

@Injectable()
export class ReportService {
    @Select(ReportState.dataReports) public dataReports$: Observable<DataReportCollection>;
    @Select(ReportState.featureReports) public featureReports$: Observable<FeatureReportCollection>;
    @Select(ReportState.featureReportKeys) public featureReportKeys$: Observable<(arg: string) => FeatureReportKeys[]>;
    @Select(AuthState.validated) public validated$: Observable<boolean>;

    @Select(SelectionState.selection) private selection$: Observable<SelectionFeatures>;

    /**
     * @param logger {LoggerService}
     * @param rest {RestService}
     * @param downloadService {DownloadService}
     * @param actions$ {Actions}
     * @param store {Store}
     * @param locale {string}
     */
    constructor(
        private logger: LoggerService,
        private rest: RestService,
        private downloadService: DownloadService,
        private actions$: Actions,
        private store: Store,
        @Inject(LOCALE_ID) private locale: string
    ) {
        this.validated$
            .pipe(
                distinctUntilChanged(),
                filter((v) => !!v),
                tap(() => setTimeout(() => this.store.dispatch(new LoadDataReports()), 0))
            )
            .subscribe();

        // set report keys when selection changes
        this.actions$
            .pipe(
                ofActionSuccessful(MapCreated),
                switchMapTo<SelectionFeatures>(this.selection$),
                map((selection) => {
                    const layers = Object.keys(selection);
                    return layers.reduce((prev, item) => {
                        const layerSelection: SelectionFeaturePayload = {
                            layer: item,
                            featureIds: selection[item],
                        };
                        prev.push(layerSelection);
                        return prev;
                    }, [] as SelectionFeaturePayload[]);
                }),
                // return them to the stream as individual items
                mergeMap((selection) => selection),
                withLatestFrom(this.featureReportKeys$),
                mergeMap(([layerSelection, currentReportKeysFn]: [SelectionFeaturePayload, (arg: string) => any[]]) => {
                    const layer = layerSelection.layer;
                    const featureIds = layerSelection.featureIds;
                    const keys: Array<string | number> = [];
                    // check if redline first
                    featureIds.forEach((featureId) => {
                        let key = featureId;
                        const testArray = featureId.split('.');
                        if (testArray.length === 2 && testArray[0].toLowerCase() === layer.toLowerCase()) {
                            key = testArray[1];
                        }
                        keys.push(key);
                    });
                    const currentReportKeys = currentReportKeysFn(layer) || [];
                    if (!isEqual([...keys].sort(), [...currentReportKeys].sort())) {
                        return this.store.dispatch(new SetFeatureReportKeys({ layer, keys }));
                    } else {
                        return of();
                    }
                })
            )
            .subscribe();
    }

    public getReportByName$(reportName: string): Observable<ReportDefinition> {
        return this.store.select((state: any) => {
            const reportState: ReportStateModel = state.reports;
            let foundReport = reportState.definitions.data.find((r) => r.name === reportName);
            if (foundReport) {
                return foundReport;
            }

            const featureReports: FeatureReportDefinitionCollection = reportState.definitions.feature;
            Object.getOwnPropertyNames(featureReports).forEach((layerName) => {
                foundReport = featureReports[layerName].find((r) => r.name === reportName);
                if (foundReport) {
                    return foundReport;
                }
            });
            return null;
        });
    }

    runReportByName(evt: { name: string; key: any; filter?: any }) {
        const reportName = evt.name;
        const key = evt.key;
        const payload: SelectReportByNamePayload = { reportName };
        if (key !== null) {
            payload.keys = [key];
        }
        if (evt.filter) {
            payload.filters = [evt.filter];
        }
        this.store.dispatch(new SelectReportByName(payload));
    }

    public downloadCSV(csvData: Blob) {
        this.downloadService.downloadBlob(csvData, 'report.csv');
    }

    public prepareExcelData(definition: ReportDefinition, reportData: Array<any>): Blob {
        // transform data to [0] header row, [...] data
        const reportFields: ReportField[] = definition.fields.filter((rf) => !rf.hidden);
        const sheet = [];
        const fields = DashGisUtil.clone(reportFields);
        sheet.push(fields.map((item) => item.display || item.name));
        reportData.forEach((row) => {
            const csvRow = [];

            fields.forEach((field: any) => {
                if (field?.hidden) {
                    return;
                }
                if (field && typeof field === 'object') {
                    switch (field.type) {
                        case 'string':
                        case 'number':
                        case 'date':
                            csvRow.push(this.getFormattedFieldValue(field, row[field.name]));
                            break;
                        case 'link':
                        case 'report':
                        case 'video':
                            csvRow.push(this.getFormattedFieldValue(field, row[field.text]));
                            break;
                    }
                } else {
                    csvRow.push(row[field]);
                }
            });
            sheet.push(csvRow);
        });
        const worksheet = XLSX.utils.aoa_to_sheet(sheet);
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const wb: XLSX.WorkBook = { SheetNames: [], Sheets: {} };
        const sheetName = definition.displayName.replace(/[\\/?*[\]]/g, '_').substring(0, 31);
        wb.SheetNames.push(sheetName);
        wb.Sheets[sheetName] = worksheet;
        const out = XLSX.write(wb, { bookType: 'xlsx', bookSST: false, type: 'binary' });
        return this.downloadService.createExcelBlob(out);
    }

    public downloadExcel(excelData: Blob, fileName = 'report') {
        this.downloadService.downloadBlob(excelData, `${fileName}.xlsx`);
    }

    public getDropDownOptions$(
        reportName: string,
        reportType: ReportTypeValue,
        filterField: string,
        filterType: string,
        parameterValue: any = null
    ): Observable<DropDownItem[]> {
        const routePrefix = reportType === 'data' ? 'report/data/options' : 'report/options';
        const route = `${routePrefix}/${reportName}/${filterField}/${filterType}`;
        const params = parameterValue ? { value: parameterValue } : null;
        return this.rest.apiGet<DropDownDataOptions>(route, params).pipe(map((ddOptions) => ddOptions.items));
    }

    private getFormattedFieldValue(field: ReportField, value: any): string {
        if ('format' in field) {
            return formatNumber(value, this.locale, field.format);
        }
        return value as string;
    }
}
